import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./history-log.module.scss";
import history from "../../../images/history.svg";
import SearchBar from "../../../components/ui-kit/search-bar/search-bar";
import arrowIcon from "../../../images/chevron.svg";
import HistoryCard from "./history-card/history-card";
import CustomScrollbar from "../../../components/ui-kit/scrollbar/scrollbar";
import { useAppSelector } from "../../../hooks/useAppSelector";
import {
  DocumentOrigin,
  HistoryLog as HistoryLogInterface,
} from "../../../interfaces/krookie";
import { HistoryLog as IHistoryLog } from "../../../interfaces/krookie";
import {
  formatDateWithoutTime,
  formatDateWithTime,
} from "../../../utils/formatDate";

const generateRevisionData = (index: string, user: string | undefined) => {
  const now = new Date();
  const formattedDate = now
    .toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    })
    .replace(/\//g, "/");
  return {
    id: -1,
    changedDateTime: formattedDate,
    changeNum: index,
    userName: user,
    comment: "",
    isGenerated: true,
  };
};

interface HistoryLogProps {
  origin: DocumentOrigin;
  documentId: number;
}

export interface FormattedHistoryLog extends HistoryLogInterface {
  formattedDate: string;
  formattedDateTime: string;
  cardTitle: string;
}

const HistoryLog: FC<HistoryLogProps> = ({ origin, documentId }) => {
  const { t } = useTranslation();
  const [isOpenHistoryLog, setIsOpenHistoryLog] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState<string>("");
  const { convertedKrookieData } = useAppSelector(
    (state) => state.masterPlanModification,
  );
  const { user } = useAppSelector((state) => state.user);
  let historyLog = convertedKrookieData.krookies[documentId]?.historyLog || [];

  if (origin === DocumentOrigin.Edit) {
    const existingNumbers = historyLog
      .map((item) => parseInt(item.changeNum, 10))
      .filter((num) => !isNaN(num));
    const maxNumber =
      existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;
    const newChangeNum = (maxNumber + 1).toString().padStart(3, "0");

    historyLog = [
      ...historyLog,
      generateRevisionData(newChangeNum, user?.userName) as IHistoryLog,
    ];
  }

  const formattedHistoryLog: FormattedHistoryLog[] = historyLog.map((item) => ({
    ...item,
    formattedDate: formatDateWithoutTime(item.changedDateTime),
    formattedDateTime: formatDateWithTime(item.changedDateTime),
    cardTitle: `${formatDateWithoutTime(item.changedDateTime)}_${item.changeNum}`,
  }));

  const toggleHistoryLog = () => {
    setIsOpenHistoryLog(!isOpenHistoryLog);
  };

  const filteredHistory = searchValue.trim()
    ? formattedHistoryLog.filter((item) => {
        const searchTerm = searchValue.toLowerCase().trim();
        const userName = item.userName?.toLowerCase() ?? "";

        return (
          item.cardTitle.toLowerCase().includes(searchTerm) ||
          item.formattedDate.toLowerCase().includes(searchTerm) ||
          userName.includes(searchTerm) ||
          item.formattedDateTime.toLowerCase().includes(searchTerm)
        );
      })
    : formattedHistoryLog;

  const sortedHistoryData = [...filteredHistory].sort(
    (a, b) => Number(b.changeNum) - Number(a.changeNum),
  );

  return (
    <div className={styles.historyLog}>
      <div
        className={`${styles.header} ${isOpenHistoryLog ? styles.open : ""}`}
        onClick={toggleHistoryLog}
      >
        <div className={styles.title}>
          <img src={history} alt={t("history-log.alt.history")} />
          <span>{t("history-log.title")}</span>
        </div>
        <img
          className={`${styles.arrow} ${isOpenHistoryLog ? styles.open : ""}`}
          src={arrowIcon}
          alt={t("history-log.alt.toggle")}
        />
      </div>
      {isOpenHistoryLog && (
        <div className={styles.content}>
          <SearchBar
            placeholder={t("history-log.search.placeholder")}
            className={styles.searchBar}
            searchValue={searchValue}
            onSearchChange={(e) => setSearchValue(e.target.value)}
          />
          <div className={styles.historyInfo}>
            <p className={styles.cardFound}>
              {t("history-log.logsFound", { count: filteredHistory.length })}
            </p>
            <div className={styles.scrollContainer}>
              <CustomScrollbar>
                <div className={styles.historyListCard}>
                  {sortedHistoryData.map((item) => (
                    <HistoryCard
                      documentId={documentId}
                      key={item.id}
                      data={item}
                    />
                  ))}
                </div>
              </CustomScrollbar>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryLog;
